import "../styles/top-bar.scss";
import { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import actions from "../actions/actions";
import Popover from "./popover";
import NotificationPanel from "./notification-panel";
import { useUpdateState } from "../utilities/utilities";
import navigate from "../utilities/navigation";
import { isMobile, Path, UserRole } from "../utilities/constants";
import OrgSwitcher from "./org-switcher";
import api from "../api/api";
import { appState } from "../redux/redux-config";

export default function TopBar() {
  let user = useSelector((store) => store.user);
  let isSidebarExpanded = useSelector((store) => store.page.sidebarExpanded);
  let updateVersion = useSelector((store) => store.page.updateVersion);
  let [state, updateState] = useUpdateState({});
  let nameRef = useRef();

  let { showHelpMenu, showSettingsMenu, showUserMenu, showNotifications } = state;
  let unreadCount = useSelector((store) => store.inAppNotifications.unreadCount);

  // Poll unread count every 60 s; fetch full list when panel opens
  useEffect(() => {
    if (!user?.token) return;
    actions.inAppNotifications.pollUnreadCount();
    const interval = setInterval(actions.inAppNotifications.pollUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user?.token]);

  function handleBellClick() {
    if (!state.showNotifications) {
      actions.inAppNotifications.fetchList();
    }
    updateState({ showNotifications: !state.showNotifications });
  }
  let showUpdateAvailable =
    updateVersion && updateVersion !== state.updateVersionDismissed;

  const [isUnityUser, setIsUnityUser] = useState(false); // Track Unity user status

  const checkIfUnityUser = async () => {
    try {
      const token = appState().user.token;
      const result = await api.user.getUserOrganizationDetails(token);
      if (result?.success) {
        setIsUnityUser(result.isUnityUser);
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
    }
  };

  useEffect(() => {
    // Check if the user is a Unity user
    checkIfUnityUser();
  }, [user]);

  if (user.loaded === undefined) setTimeout(actions.user.getUserInfo, 0);

  useEffect(() => {
    if (nameRef.current && !isMobile()) {
      nameRef.current.style.visibility = "hidden";
      for (let i = 25; i > 0; i--) {
        nameRef.current.style.fontSize = i + "px";
        if (nameRef.current.scrollWidth <= nameRef.current.clientWidth) break;
      }

      nameRef.current.style.visibility = "visible";
    } else if (nameRef.current) {
      nameRef.current.style.fontSize = "";
      nameRef.current.style.visibility = "visible";
    }

    function onResize() {
      updateState({});
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [user?.customerName, window.outerWidth, state.logoLoaded]);

  // Add module and org uuid and id once we have values .
  // useEffect(() => {
  // let script = document.createElement("script");
  // script.src = "https://account.dev1.myamplify.io/orgSwitcher/orgSwitcher.js";
  // script.id = "orgSwitcherScript";
  // script.dataset.uuid = "9DE12879-8AC2-42F1-9DCA-183B37475D13";
  // script.dataset.selected = "1";

  // hgsAppSwitcher.current.appendChild(script);
  // }, []);
  function onImageLoad() {
    updateState({ logoLoaded: true });
  }

  function onImageError() {
    updateState({ logoLoaded: false });
  }

  function goTo(e, path) {
    e.preventDefault();
    e.stopPropagation();
    updateState({ showHelpMenu: false, updateVersionDismissed: updateVersion });
    navigate(path);
  }

  function goToHelpCenter(e) {
    e.preventDefault();
    e.stopPropagation();
    window.open(user?.trainingVideoUrl);
  }

  const goToContactUs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(user?.contactUsUrl);
  };

  return (
    <div className="top-bar">
      {/* Note : commented this code for hide mobile side bar  */}
      {/* <div
        className="mobile-menu-button"
        onClick={() => dispatch("TOGGLE_SIDE_BAR", !isSidebarExpanded)}
      >
        <i className="fas fa-solid fa-bars"></i>
      </div> */}
      {isUnityUser && (
        <div className="org-and-logo">
          <div className="org-switcher-container">
            <OrgSwitcher /> {/* Only show OrgSwitcher if Unity user */}
          </div>
        </div>
      )}

      {/* Old design if not a Unity user */}
      {!isUnityUser && (
        <>
          {/* <div
            className="client-logo"
            style={{ display: state.logoLoaded ? undefined : "none" }}
          >
            <div>
              <img
                src={user?.logoUrl}
                onLoad={onImageLoad}
                onError={onImageError}
                alt="Logo"
              />
            </div>
          </div> */}
          <div className="top-bar__brand">
            {/* Home icon always visible, uses homelogo.svg */}
            <div
              className="home-icon"
              onClick={() => navigate(Path.Dashboard)}
              title="Home"
            >
              <img
                src="/lib/images/homelogo.svg"
                alt="Home"
                className="top-bar__home-img"
              />
            </div>

            {/* Always show customer name for all accounts */}
            <div className="client-name" ref={nameRef}>
              {user?.customerName}
            </div>
          </div>
        </>
      )}

      <div className="right-buttons">
        <div className="right-buttons-inner">
          <div
            className="help-icon-group"
            onClick={() => updateState({ showHelpMenu: true })}
          >
            <div className="help-icon-btn" data-hover="No">
              <div className="help-icon-inner">
                <i className="fas fa-question-circle" />
              </div>
            </div>

            <Popover
              show={showHelpMenu}
              onHide={() => updateState({ showHelpMenu: false })}
            >
              <div className="menu-item" onClick={goToHelpCenter}>
                Help Center
              </div>
              <div className="menu-item" onClick={goToContactUs}>
                Contact Us
              </div>
            </Popover>
          </div>
          {!isMobile() && user?.userType === UserRole.Admin && (
            <div
              className="cog-icon-btn"
              data-hover="No"
              onClick={() => navigate(Path.AccountGeneral)}
            >
              <div className="cog-icon-inner">
                <i className="fas fa-cog" />
              </div>
            </div>
          )}
          <div
            className="bell-icon-btn"
            data-hover="No"
            onClick={handleBellClick}
          >
            <div className="bell-icon-inner">
              <i className="fas fa-bell" />
            </div>
            {unreadCount > 0 && (
              <div className="bell-icon-badge">
                {unreadCount > 99 ? "99+" : unreadCount}
              </div>
            )}
            <Popover
              show={showNotifications}
              onHide={() => updateState({ showNotifications: false })}
              className="notif-popover"
            >
              <NotificationPanel
                onClose={() => updateState({ showNotifications: false })}
              />
            </Popover>
          </div>

          <div
            className="user-avatar"
            onClick={() => updateState({ showUserMenu: !showUserMenu })}
          >
            <div className="user-avatar-bg" />
            <div className="user-avatar-initials">
              {user.firstName?.substr(0, 1) + user.lastName?.substr(0, 1) ||
                "\u00A0"}
            </div>
            <Popover
              show={showUserMenu}
              onHide={() => updateState({ showUserMenu: false })}
            >
              <div className="menu-item" onClick={actions.user.logout}>
                Log Out
              </div>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}
